<template>
  <validation-observer
    ref="refinance-modal"
    v-slot="{ invalid }"
  >
    <b-modal
      id="model-pay-principal"
      :title="`${$t('Repay principal')} (${$t(customerData.loan.loanType || 'balloon')})`"
      :hide-header-close="true"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      footer-class="justify-content-end flex-row-reverse"
      :ok-title="$t('pay')"
      :ok-disabled="invalid"
      :cancel-title="$t('cancel')"
      cancel-variant="outline-secondary"
      @ok="handleSubmit"
      @hidden="resetForm"
    >
      <b-form>
        <b-row>
          <b-col cols="6">
            <b-form-group
              :label="$t('date')"
              label-for="add-date"
            >
              <b-form-datepicker
                id="add-date"
                v-model="paidInfo.date"
              />
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group
              :label="$t('time')"
              label-for="add-time"
            >
              <b-form-timepicker
                id="add-time"
                v-model="paidInfo.time"
                locale="en"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <template v-if="customerData">
          <b-row v-if="hasDueDateTime">
            <b-col>
              {{ $t('due date') + ': ' }}
              <span :class="{ 'text-muted': paidInfo.nextPeriod }">
                {{ formatDateTime(customerData.loan.dueDateTime) }}
              </span>
              <template v-if="paidInfo.nextPeriod">
                &nbsp;→&nbsp;{{ calculateNextDueDate }}
              </template>
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              {{ $t('principal') + ': ' }}{{ formatNumber(customerData.loan.principal) }}
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              {{ $t('payment duration') + ': ' }}{{ formatNumber(customerData.loan.paymentDuration) }}
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              {{ $t('interest rate') + ': ' }}{{ formatNumber(customerData.loan.interestRate) }}%
            </b-col>
          </b-row>

          <b-row>
            <b-col>
              {{ $t('repayment term') + ': ' }}
              {{ customerData.loan.periods ? formatNumber(customerData.loan.periods) : $t('none') }}
            </b-col>
          </b-row>

          <b-row v-if="customerData.loan.periods">
            <b-col>
              {{ $t('remaining repayment periods') + ': ' }}
              <span :class="{ 'text-muted': paidInfo.nextPeriod }">
                {{ formatNumber(customerData.loan.remainingPeriods) }}
              </span>
              <template v-if="paidInfo.nextPeriod">
                &nbsp;→&nbsp;{{ formatNumber(customerData.loan.remainingPeriods - 1) }}
              </template>
            </b-col>
          </b-row>

          <b-row class="pb-1">
            <b-col>
              {{ $t('payable') + ': ' }}
              <span :class="{ 'text-muted': paidInfo.adjustPayable }">
                {{ formatNumber(customerData.loan.payable) }}
              </span>
              <template v-if="paidInfo.adjustPayable">
                &nbsp;→&nbsp;{{ calculateAdjustedPayable }}
              </template>
            </b-col>
          </b-row>
        </template>

        <b-row>
          <b-col>
            <validation-provider
              #default="{ errors }"
              :name="$t('amount')"
              rules="required|positive"
            >
              <b-form-group
                :label="$t('amount')"
                label-for="paid-amount"
              >
                <b-input-group append=".00">
                  <b-form-input
                    id="paid-amount"
                    v-model="paidInfo.paid"
                  />
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </b-form-group>
            </validation-provider>
          </b-col>
        </b-row>

        <template v-if="customerData && paidInfo.paid">
          <b-row class="pb-1">
            <b-col>
              {{ $t('Remaining principal') + ': ' }}
              <template v-if="calculateRemainingPrincipal <= 0">
                <span class="text-success">{{ $t('closed') }}</span>
              </template>
              <template v-else>
                {{ formatNumber(calculateRemainingPrincipal) }}
              </template>
              <template v-if="calculateRemainingPrincipal < 0">
                ({{ $t('allowance') }}: {{ formatNumber(Math.abs(calculateRemainingPrincipal)) }})
              </template>
              <template v-if="isAmortization && hasDueDateTime && $moment(customerData.loan.dueDateTime).isSameOrBefore(paidInfo.date) && paidInfo.paid < customerData.loan.payable">
                <span class="text-danger">({{ $t('penalty') }}: {{ formatNumber(subtract(customerData.loan.payable, paidInfo.paid)) }})</span>
              </template>
            </b-col>
          </b-row>

          <b-row v-if="isAmortization">
            <b-col>
              <b-form-group>
                <b-form-checkbox
                  v-model="paidInfo.nextPeriod"
                  name="next-period"
                >
                  {{ $t('next payment period') }}
                  <small class="text-muted ml-2">
                    ({{ $t('next payment') }} = {{ $t('due date') }} + {{ $t('payment duration') }})
                  </small>
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-if="calculateRemainingPrincipal > 0">
            <b-col>
              <b-form-group>
                <b-form-checkbox
                  v-model="paidInfo.adjustPayable"
                  name="adjust-payable"
                >
                  {{ $t('adjust payable') }}
                  <small class="text-muted ml-2">
                    {{ adjustPayableFormula }}
                  </small>
                </b-form-checkbox>
              </b-form-group>
            </b-col>
          </b-row>
        </template>

        <b-row>
          <b-col>
            <b-form-group
              :label="$t('note')"
              label-for="textarea-note"
            >
              <b-form-textarea
                id="textarea-note"
                v-model="paidInfo.note"
                rows="3"
                :placeholder="$t('content')"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </validation-observer>
</template>

<script>
import {
  ValidationProvider, ValidationObserver, localize, extend,
} from 'vee-validate'
import { required, positive } from '@validations'
import * as rules from 'vee-validate/dist/rules'
import store from '@/store'
import {
  BRow, BCol, BModal, VBModal, BFormGroup, BFormTextarea, BFormDatepicker,
  BFormInput, BFormTimepicker, BForm, BInputGroup, BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { round, subtract } from 'mathjs'

localize('tw')
Object.keys(rules).forEach(rule => {
  extend(rule, rules[rule])
})

export default {
  name: 'PayPrincipal',

  components: {
    BRow,
    BCol,
    BForm,
    BInputGroup,
    BModal,
    BFormGroup,
    BFormTextarea,
    BFormInput,
    BFormDatepicker,
    BFormTimepicker,
    ValidationProvider,
    ValidationObserver,
    BFormCheckbox,
  },

  directives: {
    'b-modal': VBModal,
    Ripple,
  },

  props: {
    customerData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    userData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },

  data() {
    return {
      subtract,
      positive,
      required,
      paidInfo: this.getDefaultPaidInfo(),
    }
  },

  computed: {
    isAmortization() {
      return this.customerData?.loan?.loanType === 'amortization'
    },

    hasDueDateTime() {
      return this.customerData?.loan?.dueDateTime && this.customerData.loan.dueDateTime !== ''
    },

    calculateNextDueDate() {
      return this.$moment(this.customerData.loan.dueDateTime)
        .add(this.customerData.loan.paymentDuration, 'days')
        .format('YYYY-MM-DD HH:mm')
    },

    calculateRemainingPrincipal() {
      return this.customerData.loan.principal - this.paidInfo.paid
    },

    calculateAdjustedPayable() {
      if (this.isAmortization) {
        return round((this.customerData.loan.principal - this.paidInfo.paid) / (this.paidInfo.nextPeriod ? this.customerData.loan.remainingPeriods - 1 : this.customerData.loan.remainingPeriods))
      }
      return round((this.customerData.loan.principal
        - this.paidInfo.paid) * (this.customerData.loan.interestRate / 100))
    },

    adjustPayableFormula() {
      if (this.isAmortization) {
        return `(${this.$t('pay per period')} = (${this.$t('principal')} - ${this.$t('amount')}) / ${this.$t('remaining repayment periods')})`
      }
      return `(${this.$t('pay per period')} = (${this.$t('principal')} - ${this.$t('amount')}) × ${this.$t('interest rate')})`
    },
  },

  methods: {
    getDefaultPaidInfo() {
      return {
        date: this.$moment().format('YYYY-MM-DD'),
        time: this.$moment().format('HH:mm'),
        paid: '',
        note: '',
        adjustPayable: false,
        nextPeriod: true,
      }
    },

    resetForm() {
      this.paidInfo = this.getDefaultPaidInfo()
    },

    formatDateTime(dateTime) {
      return this.$moment(dateTime).format('YYYY-MM-DD HH:mm')
    },

    formatNumber(value) {
      return this.numberWithCommas(value)
    },

    async handleSubmit() {
      if (!this.customerData || !this.userData || !this.paidInfo.paid) {
        return
      }

      const dateTime = this.$moment(
        `${this.paidInfo.date} ${this.paidInfo.time}`,
        'YYYY-MM-DD HH:mm',
      ).format()

      const data = {
        ...this.userData,
        customer: this.customerData,
        cid: this.customerData.cid,
        paid: this.paidInfo.paid,
        note: this.paidInfo.note,
        dateTime,
        adjustPayable: this.paidInfo.adjustPayable,
        nextPeriod: this.paidInfo.nextPeriod,
      }

      await store.dispatch('payPrincipal', data)
      this.resetForm()
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-context.scss';
</style>
